import { gradeMatchesPeriod, getGroupGrades, getStudentGrades } from "helpers/gradebook"
import { exportBook, addRange, exportCSV} from "external/sheetjs"
import { formatDate } from "external/date-fns"

export function exportYearRollbook(context, data, groups, rollbookLabel, format = 'xlsx', includeIgnored = false) {

    const {grades = [], gradeColumns = [], properties = {}} = data

    const studentGrades = groups.map(group => {
        const groupGrades = grades.filter(g => g.group == group.id) || []
        const groupColumns = gradeColumns.filter(g => g.group == group.id) || []
        const findStudentGrades = getGroupGrades(groupGrades)
        const filteredColumns = includeIgnored ? groupColumns : groupColumns.filter(c => !c.properties.ignore)
        return {...group, grades: getStudentGrades(group.students, filteredColumns, findStudentGrades, properties)}
    })

    const bookFile = `${rollbookLabel}-${formatDate(new Date(), 'yyyy-MM-dd')}.${format}`
    const sheets = studentGrades.map(group => groupSheet(context, group.grades, group.id))
    switch (format) {
        case 'csv':        
            const jsonCSV = flattenStudentGrades(context, sheets)
            return exportCSV(jsonCSV, {filename: bookFile}) 
        default:
            return exportBook(bookFile, sheets, handleRollbookProcessing)

    }

}



export async function exportGroupRollbook(context, grades) {
    const [name, sheet] = groupSheet(context, grades, context.groupId)
    const bookName= `${name}-${formatDate(new Date(), 'yyyy-MM-dd')}`
    const bookFile = `${bookName}.xlsx`
    return exportBook(bookFile, [[name, sheet]], handleRollbookProcessing)
}

function flattenStudentGrades(context, sheets) {

    const groupHeader = context.translate('export-rollbook-csv-group-header')
    const periodHeader = context.translate('export-rollbook-csv-period-header')
    const dateHeader = context.translate('export-rollbook-csv-date-header')
    const nameHeader = context.translate('export-rollbook-csv-name-header')
    const totalHeader = context.translate('export-rollbook-csv-total-header')
    const studentHeader = context.translate('export-rollbook-csv-student-header')
    const pointsHeader = context.translate('export-rollbook-csv-points-header')

    const jsonCSV = sheets.reduce((csv, [groupName, rows]) => {

        const [_, periodRow, dateRow, titleRow, totalPointsRow, ...studentRows] = rows

        const findPeriod = index => {
            for (let i = index + 1; i > 0; i--) {
                const period = periodRow[i] || {set: ''}
                if (!/^\s*$/.test(period.set)) return period.set;
            }
           return '' 
        }

        return csv.concat(studentRows.reduce((sheet, [studentName, ...studentGrades]) => {
            return sheet.concat(studentGrades.reduce((grades, points, index) => {
                const row = {
                    [groupHeader]: groupName,
                    [periodHeader]: findPeriod(index),
                    [dateHeader]: dateRow[index + 1].set,
                    [nameHeader]: titleRow[index + 1].set,
                    [totalHeader]: totalPointsRow[index + 1].set,
                    [studentHeader]: studentName.set,
                    [pointsHeader]: points.set
                }
                return [...grades, row]
            }, []))
        }, []))

    }, [])

    return jsonCSV
}

function groupSheet(context, grades, groupId) {
    const {translate, groups} = context
    const sheetGroup = groups.find(group => group.id == groupId)
    const sheetGroupName = sheetGroup && sheetGroup.name
    const [firstGrade] = grades
    const sheet = sheetHeaders(translate, sheetGroupName)
    if (!firstGrade) return [sheetGroupName, sheet]
    const withPeriods = addRange(sheet, periodColumns(context, firstGrade), {column: 1, row: 1})
    const withGradeColumns = addRange(withPeriods, sheetGradeColumns(context, firstGrade), {column: 1, row: 2})
    const withGrades = addRange(withGradeColumns, sheetGradeRows(context, grades), {column: 0, row: 5})
    return [sheetGroupName, withGrades]
}

function periodColumns(context, firstGrade) {
    const {items: periods} = context.rollbookContextPeriods

    const defaults = {
        align: 'left',
        valign: 'center',
        font: { name: 'Verdana', sz: 11, bold: true, iter: true}
    }

    const cell = (set, styles) => ({set, ...defaults, ...styles})

    return [firstGrade.columns.reduce((range, column) => {
        const period = periods.find(gradeMatchesPeriod(column))
        const missingName = period && !range.find(a => a.set == period.properties.name)
        range.push(missingName ? cell(period.properties.name) : '')
        return range
    }, [])]
}

function sheetHeaders(translate, groupName) {

    const fontStyles = (sz = 11) => ({font: { bold: true, sz, name: 'Verdana'}})
    const valign = 'center'
    const height = 30
    return [
        [{set: groupName, ...fontStyles(12), align: 'center', valign: 'center'}],
        [{height, valign, set: translate('export-rollbook-group-period'), ...fontStyles()}],
        [{height, valign, set: translate('export-rollbook-group-date'), ...fontStyles()}],
        [{height, valign, set: translate('export-rollbook-group-name'), ...fontStyles()}],
        [{height, valign, set: translate('export-rollbook-group-total-score'), ...fontStyles()}],
    ]
}

const ignoreFontStyles = {
    color: 'aea5a5'
}

const ignoreBackgroundStyles = {
    fill: {type: 'solid', fgColor: 'd6d6d6'}
}

function sheetGradeColumns(context, firstGrade) {
    const {columns = []} = firstGrade

    const defaults = {
        align: 'center',
        valign: 'center'
    }

    const cell = (set, styles) => ({set, ...defaults, ...styles})

    return columns.reduce((range, column) => {
        const {ignore} = column.properties
        const [dates, names, scores] = range
        dates.push(cell(formatDate(column.start, 'yyyy-MM-dd'), {
            font:{name: 'Roboto', sz: 10.5, bold: true,
               ...(ignore ? ignoreFontStyles : {})
            },
            ...(ignore ? ignoreBackgroundStyles : {})
        }))
        names.push(cell(`${column.label}: ${column.title}`, {align: 'left', ...(ignore ? {font: ignoreFontStyles, ...ignoreBackgroundStyles} : {})}))
        scores.push(cell(column.total, {font: {name: 'Roboto', sz: 11, bold: true}}))
        return range
    }, [[], [], []])
}

function sheetGradeRows(context, grades) {

    const defaults = {
        align: 'center',
        valign: 'center',
        font: {name: 'Roboto', sz: 11}
    }

    const cell = (set, styles) => ({set, ...defaults, ...styles})

    return grades.reduce((range, student) => {
        range.push([cell(student.name, {font: {name: 'Verdana', sz: 11, iter: true}, align: 'left'})].concat(student.columns.map(c => cell(c.points))))
        return range
    }, [])
}

function handleRollbookProcessing ({type, target, context}) {
    if (type == 'pre' && target == 'row') {
        const {sheet, rowIndex} = context
        const rowHeight = rowIndex < 5 ? 30 : 25
        sheet.height(rowIndex + 1, rowHeight)
    }
    if (type == 'post' && target == 'column') {
        const {columnIndex, rowIndex, sheet} = context
        if (columnIndex == 0) sheet.width(columnIndex + 1, 30)
        else sheet.width(columnIndex + 1, 20)
        if (rowIndex == 2) sheet.wrap(columnIndex + 1, rowIndex + 1, 'true')
    }
    if (type == 'post' && target == 'sheet') {
        const {sheet} = context
        sheet.height(1, 35)
    }
}